export const FRANCHISE_PLAYER_POSITIONS = [
  'QB',
  'RB',
  'FB',
  'WR',
  'TE',
  'OT',
  'OG',
  'C',
  'DE',
  'DT',
  'LB',
  'CB',
  'S',
  'K',
  'P',
] as const;

export type FranchisePlayerPosition = typeof FRANCHISE_PLAYER_POSITIONS[number];

export const PLAYER_STAT_KEYS = [
  'athleticism',
  'strength',
  'precision',
  'awareness',
  'physicality',
] as const;

export type PlayerStatKey = typeof PLAYER_STAT_KEYS[number];

export type PlayerRatings = Partial<Record<PlayerStatKey, number>> & {
  athleticism: number;
};

export const PLAYER_PERSONALITY_ARCHETYPES = [
  'steady',
  'leader',
  'showman',
  'competitor',
  'volatile',
] as const;

export type PlayerPersonalityArchetype = typeof PLAYER_PERSONALITY_ARCHETYPES[number];

export type PlayerPersonality = {
  archetype: PlayerPersonalityArchetype;
  leadership: number;
  composure: number;
  competitiveness: number;
  showmanship: number;
  loyalty: number;
  volatility: number;
  workEthic: number;
  celebration?: 'none' | 'team' | 'calm' | 'power' | 'dance' | 'signature' | 'goalpost';
};

export type PlayerAvailability = 'healthy' | 'limited' | 'out' | 'retired';

export type PlayerCareerState = {
  rookieSeason?: string;
  age?: number;
  experience?: number;
  availability?: PlayerAvailability;
  seasonLoad?: number;
  readiness?: number;
  injuryNote?: string;
  retiredSeason?: string;
};

export type FranchisePlayer = {
  id: string;
  name: string;
  position: FranchisePlayerPosition;
  years: number;
  number: number;
  overall: number;
  ratings: PlayerRatings;
  personality?: PlayerPersonality;
  career?: PlayerCareerState;
  /** Owner-selectable football role. Ratings still determine ability; style shapes usage and tendencies. */
  playStyle?: import('./player-play-style').PlayerPlayStyle;
  /**
   * Lowercase wallet address currently holding this player's NFT, stamped onto
   * the pool record by the admin owner-sync. Absent for non-NFT players and
   * for NFTs the sync found unowned.
   */
  ownerWallet?: string;
  appearance?: {
    skinColor?: string;
    hairColor?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

/**
 * Rating written into every stat of an auto-fill placeholder player (the
 * nameless depth generated when a roster is short of the franchise position
 * minimums). Tune it here — nothing else hardcodes the value.
 */
export const AUTO_FILL_PLAYER_RATING = 50;

/**
 * Id prefix marking generated placeholder players. Auto-fill players are never
 * persisted — they exist only on in-memory sim rosters — so the prefix keeps
 * them distinguishable from real pool ids (seeded or NFT tokenIds).
 */
export const AUTO_FILL_PLAYER_ID_PREFIX = 'auto-fill:';

export function isAutoFillPlayer(player: Pick<FranchisePlayer, 'id'> | null | undefined): boolean {
  return typeof player?.id === 'string' && player.id.startsWith(AUTO_FILL_PLAYER_ID_PREFIX);
}

export const FRANCHISE_JERSEY_NUMBER_RANGES: Record<FranchisePlayerPosition, readonly [number, number]> = {
  QB: [1, 19],
  RB: [20, 49],
  FB: [30, 49],
  WR: [10, 19],
  TE: [80, 89],
  OT: [60, 79],
  OG: [60, 79],
  C: [50, 69],
  DE: [90, 99],
  DT: [70, 99],
  LB: [40, 59],
  CB: [20, 49],
  S: [20, 49],
  K: [1, 19],
  P: [1, 19],
};

export type PlayerStatDefinition = {
  label: string;
  shortLabel: string;
  description: string;
};

export const PLAYER_STAT_DEFINITIONS: Record<PlayerStatKey, PlayerStatDefinition> = {
  athleticism: {
    label: 'Athleticism',
    shortLabel: 'ATH',
    description: 'Speed, agility, and stamina — burst, change of direction, open-field movement, and endurance.',
  },
  strength: {
    label: 'Strength',
    shortLabel: 'STR',
    description: 'Trench power, run and pass blocking, pass protection, anchor, and displacement ability.',
  },
  precision: {
    label: 'Precision',
    shortLabel: 'PRE',
    description: 'Throwing and kicking accuracy, catching, ball handling, leg power, and hang time.',
  },
  awareness: {
    label: 'Awareness',
    shortLabel: 'AWR',
    description: 'Decision making, vision, coverage instincts, and composure under pressure.',
  },
  physicality: {
    label: 'Physicality',
    shortLabel: 'PHY',
    description: 'Tackling, pass rush, run containment, and finishing in traffic.',
  },
};

export const PLAYER_POSITION_STAT_KEYS: Record<FranchisePlayerPosition, readonly PlayerStatKey[]> = {
  QB: ['strength', 'precision', 'awareness', 'athleticism'],
  RB: ['athleticism', 'awareness', 'precision', 'strength'],
  FB: ['athleticism', 'awareness', 'precision', 'strength'],
  WR: ['athleticism', 'precision', 'strength'],
  TE: ['athleticism', 'precision', 'strength'],
  OT: ['strength', 'athleticism'],
  OG: ['strength', 'athleticism'],
  C: ['strength', 'athleticism'],
  DE: ['physicality', 'strength', 'athleticism'],
  DT: ['physicality', 'strength', 'athleticism'],
  LB: ['athleticism', 'physicality', 'awareness'],
  CB: ['athleticism', 'awareness', 'precision', 'physicality'],
  S: ['athleticism', 'awareness', 'precision', 'physicality'],
  K: ['precision', 'awareness', 'athleticism'],
  P: ['precision', 'athleticism'],
};

export const PLAYER_POSITION_GROUP_LABELS: Record<FranchisePlayerPosition, string> = {
  QB: 'Quarterback',
  RB: 'Running Back',
  FB: 'Fullback',
  WR: 'Wide Receiver',
  TE: 'Tight End',
  OT: 'Offensive Tackle',
  OG: 'Offensive Guard',
  C: 'Center',
  DE: 'Defensive End',
  DT: 'Defensive Tackle',
  LB: 'Linebacker',
  CB: 'Cornerback',
  S: 'Safety',
  K: 'Kicker',
  P: 'Punter',
};

export const PLAYER_OVERALL_WEIGHTS: Record<FranchisePlayerPosition, ReadonlyArray<readonly [PlayerStatKey, number]>> = {
  QB: [
    ['strength', 0.28],
    ['precision', 0.31],
    ['awareness', 0.25],
    ['athleticism', 0.16],
  ],
  RB: [
    ['athleticism', 0.45],
    ['awareness', 0.22],
    ['precision', 0.28],
    ['strength', 0.05],
  ],
  FB: [
    ['athleticism', 0.28],
    ['awareness', 0.16],
    ['precision', 0.28],
    ['strength', 0.28],
  ],
  WR: [
    ['athleticism', 0.53],
    ['precision', 0.43],
    ['strength', 0.04],
  ],
  TE: [
    ['athleticism', 0.36],
    ['precision', 0.38],
    ['strength', 0.26],
  ],
  OT: [
    ['strength', 0.91],
    ['athleticism', 0.09],
  ],
  OG: [
    ['strength', 0.9],
    ['athleticism', 0.1],
  ],
  C: [
    ['strength', 0.87],
    ['athleticism', 0.13],
  ],
  DE: [
    ['physicality', 0.68],
    ['strength', 0.22],
    ['athleticism', 0.1],
  ],
  DT: [
    ['physicality', 0.64],
    ['strength', 0.26],
    ['athleticism', 0.1],
  ],
  LB: [
    ['athleticism', 0.3],
    ['physicality', 0.52],
    ['awareness', 0.18],
  ],
  CB: [
    ['athleticism', 0.5],
    ['awareness', 0.31],
    ['precision', 0.12],
    ['physicality', 0.07],
  ],
  S: [
    ['athleticism', 0.42],
    ['awareness', 0.25],
    ['precision', 0.11],
    ['physicality', 0.22],
  ],
  K: [
    ['precision', 0.74],
    ['awareness', 0.18],
    ['athleticism', 0.08],
  ],
  P: [
    ['precision', 0.88],
    ['athleticism', 0.12],
  ],
};

function clampRating(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(99, Math.round(value)));
}

function weightedAverage(values: ReadonlyArray<readonly [number, number]>): number {
  let weightedTotal = 0;
  let weightTotal = 0;
  values.forEach(([value, weight]) => {
    if (!Number.isFinite(value) || !Number.isFinite(weight) || weight <= 0) {
      return;
    }
    weightedTotal += value * weight;
    weightTotal += weight;
  });

  if (weightTotal <= 0) {
    return 0;
  }

  return weightedTotal / weightTotal;
}

function rawRating(ratings: Record<string, unknown> | null | undefined, key: string, fallback: number = 0): number {
  const value = ratings?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizePosition(value: unknown): FranchisePlayerPosition {
  const candidate = String(value || '').toUpperCase();
  if ((FRANCHISE_PLAYER_POSITIONS as readonly string[]).includes(candidate)) {
    return candidate as FranchisePlayerPosition;
  }

  if (candidate === 'FS' || candidate === 'SS') {
    return 'S';
  }

  return 'WR';
}

export function getPlayerRating(player: { ratings?: Record<string, unknown> | null | undefined } | null | undefined, key: PlayerStatKey, fallback: number = 0): number {
  const value = player?.ratings?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function getPlayerStatKeys(position: FranchisePlayerPosition | string): readonly PlayerStatKey[] {
  return PLAYER_POSITION_STAT_KEYS[normalizePosition(position)];
}

export function getPlayerStatDefinition(key: PlayerStatKey): PlayerStatDefinition {
  return PLAYER_STAT_DEFINITIONS[key];
}

export function getPlayerRatingColumns(position: FranchisePlayerPosition | string): Array<{ key: PlayerStatKey; label: string; description: string }> {
  return getPlayerStatKeys(position).map((key) => ({
    key,
    label: PLAYER_STAT_DEFINITIONS[key].shortLabel,
    description: PLAYER_STAT_DEFINITIONS[key].description,
  }));
}

export function calculatePlayerOverall(position: FranchisePlayerPosition | string, ratings: PlayerRatings): number {
  const resolvedPosition = normalizePosition(position);
  const weights = PLAYER_OVERALL_WEIGHTS[resolvedPosition];
  const overall = weightedAverage(weights.map(([key, weight]) => [rawRating(ratings, key), weight] as const));
  return clampRating(overall);
}

export function normalizePlayerRatings(_position: FranchisePlayerPosition | string, ratings: Record<string, unknown> | null | undefined): PlayerRatings {
  const normalized = PLAYER_STAT_KEYS.reduce((result, key) => {
    result[key] = clampRating(rawRating(ratings, key));
    return result;
  }, {} as Partial<Record<PlayerStatKey, number>>);

  normalized.athleticism = clampRating(rawRating(ratings, 'athleticism', normalized.athleticism ?? 75));
  return normalized as PlayerRatings;
}

export function normalizeFranchisePlayer(player: FranchisePlayer): FranchisePlayer {
  const position = normalizePosition(player.position);
  const ratings = normalizePlayerRatings(position, player.ratings as Record<string, unknown> | null | undefined);
  return {
    ...player,
    position,
    years: typeof player.years === 'number' && Number.isFinite(player.years) ? player.years : 1,
    number: typeof player.number === 'number' && Number.isFinite(player.number) ? player.number : 0,
    ratings,
    overall: calculatePlayerOverall(position, ratings),
    appearance: player.appearance ? { ...player.appearance } : undefined,
    personality: player.personality ? { ...player.personality } : undefined,
    career: player.career ? { ...player.career } : undefined,
  };
}

export function normalizeFranchiseRoster(players: Array<FranchisePlayer>): FranchisePlayer[] {
  return players.map((player) => normalizeFranchisePlayer(player));
}

export function deriveReturnAbility(player: FranchisePlayer | null | undefined): number {
  if (!player) {
    return 0;
  }

  const weightedStats: Array<readonly [number, number]> = [
    [getPlayerRating(player, 'athleticism'), 0.5],
    [getPlayerRating(player, 'precision'), 0.34],
  ];

  const awareness = getPlayerRating(player, 'awareness');
  if (awareness > 0) {
    weightedStats.push([awareness, 0.16]);
  }

  return clampRating(weightedAverage(weightedStats));
}
